import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../redux/hooks";
import { useFetch } from "../../../../hooks/api";
import { useDates } from "../../../../dates/DatesContext";
import moment from "moment";
import { snakeCaseToSentenceCase } from "../../../../utils/formatText";
import { SEARCH_TERM_REPORT_MATCH_TYPE_OPTIONS } from "../../../../modules/advertising/filters/targetLifecycleFilters";
import { Col, Row } from "react-bootstrap";
import FormGroupTile from "../../basic/FormGroupTile";
import KeywordCell from "../../../custom/category/advertisements/KeywordCell";
import { useAcosOrRoas } from "../../../../utils/acosOrRoas";
import { useStandardEntityMetrics } from "../../../../modules/merchandising/components/tables/metricUtils";
import { MdRotateLeft } from "react-icons/md";
import { Button } from "../../basic/Button";
import { RT8ColumnSelector } from "../MetricSelector";
import { SearchInput } from "../../../fields/base/TextField";
import { RT8FilterWidget } from "../../../../modules/merchandising/components/tables/FilterWidget";
import { useClientTable } from "../../../table/tableFeatures";
import { createColumnBuilder } from "../../../table/columnBuilder";
import { customFilterFn, commaSearchFilterFn } from "../../../table/filterFns";
import { resetTable } from "../../../table/utils";
import { RT8Pagination } from "../../../table/Pagination";
import { Table } from "../../../table/Table";
import { HStack, VStack } from "../../../../../styled-system/jsx";
import { css } from "../../../../../styled-system/css";
import DownloadReport from "../../../../modules/perf_reports/DownloadReport";
import SearchTermReportDashboardTable from "../../../../modules/str/SearchTermReportDashboardTable";
import useSearchTermReportColumns from "../../../../modules/str/useSearchTermReportColumns";
import { idsToVisibilityState } from "../../../table/utils";
import { usePersistedTableState } from "../../../table/usePersistedTableState";
import { useSimpleCan } from "../../../../config/casl/casl";

const CampaignSelector = ({ campaignOptions, selectedCampaignId, setSelectedCampaignId }) => (
  <FormGroupTile
    type="select"
    multi={false}
    stateName={"campaignId"}
    handleChildFormElement={(key, value) => {
      setSelectedCampaignId(value.value);
    }}
    defaultValue={campaignOptions?.find((option) => option.value === selectedCampaignId)}
    options={campaignOptions}
  />
);

const columnHelper = createColumnBuilder();

const searchTermSearchFilterFn = commaSearchFilterFn(
  (row) => `${row?.query} ${row.keyword_text} ${row.match_type} ${row?.searched_keyword} ${row?.bidded_keyword}`,
);

const WalmartSearchTermTable = ({ data, isLoading, isWalmartReport, isAutoCampaign }) => {
  const { t } = useTranslation(["advertising", "common"]);
  const acosOrRoas = useAcosOrRoas();
  const standardMetricIds = useStandardEntityMetrics("searchTerm");

  const titleColId = isWalmartReport ? "searched_keyword" : "query";

  const columns = useMemo(
    () => [
      columnHelper.accessor(titleColId, {
        header: t(($) => $.advertising["Query"]),
        size: 250,
        enableHiding: false,
        sortFn: "alphanumeric",
        cell: ({ getValue, column }) => <KeywordCell value={getValue()} columnWidth={column.getSize()} />,
      }),
      columnHelper.accessor(isWalmartReport ? "bidded_keyword" : "keyword_text", {
        header: t(($) => $.advertising["Keyword Text"]),
        size: 200,
        sortFn: "alphanumeric",
        meta: {
          columnSelectorOptions: {
            description: t(($) => $.advertising["The target or keyword that matches a user-provided search term"]),
          },
        },
      }),
      ...(isAutoCampaign ?
        []
      : [
          columnHelper.accessor((row) => row.match_type?.toLowerCase(), {
            id: "match_type",
            header: t(($) => $.advertising["Match Type"]),
            size: 130,
            enableResizing: false,
            sortFn: "alphanumeric",
            cell: ({ getValue }) => snakeCaseToSentenceCase(getValue())?.toLowerCase(),
            meta: { columnFilterOptions: { format: "single-select" } },
          }),
        ]),
      ...(isWalmartReport ?
        [
          columnHelper.accessor("item_id", {
            header: t(($) => $.advertising["Item ID"]),
            size: 130,
            sortFn: "alphanumeric",
          }),
        ]
      : []),
      ...standardMetricIds.map((metricId) => columnHelper.metric(metricId)),
      // Walmart returns units; the standard search term list is built for the Amazon report.
      ...(isWalmartReport ? [columnHelper.metric("units")] : []),
    ],
    [isAutoCampaign, isWalmartReport, standardMetricIds, t, titleColId],
  );

  // Below is to hide additional metrics by default without having to define each one
  const defaultSelectedColumns = [
    titleColId,
    isWalmartReport ? "bidded_keyword" : "keyword_text",
    "match_type",
    "ad_spend",
    "ad_sales",
    "acos",
    "roas",
    "clicks",
    "impressions",
    "viewable_impressions",
    "viewability_rate",
    "view_click_through_rate",
    "units",
  ];
  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id),
  );

  const initialStateConfig = {
    columnVisibility: {
      ...defaultHiddenCols,
      acos: acosOrRoas(true, false),
      roas: acosOrRoas(false, true),
    },
    columnOrder: defaultSelectedColumns,
    sorting: [{ id: "ad_sales", desc: true }],
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "searchTermReport",
    initialState: initialStateConfig,
  });

  const table = useClientTable({
    data,
    columns,
    defaultColumn: {
      filterFn: customFilterFn,
    },
    globalFilterFn: searchTermSearchFilterFn,
    initialState,
    state,
    ...setters,
    meta: {
      showSkeleton: isLoading,
      columnFilterSelectOptions: {
        match_type: SEARCH_TERM_REPORT_MATCH_TYPE_OPTIONS.map(({ value, label }) => ({ value, label: t(label) })),
      },
    },
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              aria-label={t(($) => $.common.table["Reset table filters"])}
              onPress={() => resetTable(table)}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter ?? ""}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
    </>
  );
};

const SearchTermReport = ({ adPlanParams, adId }) => {
  const { t } = useTranslation(["advertising"]);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel);
  const { start, end } = useDates();
  const userCanExportAds = useSimpleCan("export", "Ad");
  const acosOrRoas = useAcosOrRoas();

  const isWalmartReport = channel?.currentChannel === "walmart";
  const campaigns = useMemo(() => adPlanParams?.campaignIds ?? {}, [adPlanParams?.campaignIds]);

  const campaignOptions = useMemo(() => {
    const options = [];
    const CAMPAIGN_TYPE_LABELS = {
      manual: ($) => $.advertising["Manual Campaign"],
      auto: ($) => $.advertising["Auto Campaign"],
      contextual: ($) => $.advertising["Contextual Campaign"],
      audience: ($) => $.advertising["Audience Campaign"],
    };

    for (const campaignType in campaigns) {
      const typeKey = campaignType?.split("_")?.[isWalmartReport ? 1 : 0];
      const labelSelector = CAMPAIGN_TYPE_LABELS[typeKey];
      options.push({
        label:
          labelSelector ?
            t(labelSelector)
          : `${snakeCaseToSentenceCase(typeKey)} ${t(($) => $.advertising["Campaign"])}`,
        value: campaigns[campaignType],
      });
    }

    if ((!selectedCampaignId && options.length) || !options?.map((o) => o.value)?.includes(selectedCampaignId)) {
      setSelectedCampaignId(options[0]?.value);
    }
    return options;
  }, [campaigns, selectedCampaignId, isWalmartReport, t]);

  const startDate = moment(start).format("YYYY-MM-DD");
  const endDate = moment(end).format("YYYY-MM-DD");

  const reportEndpoint =
    isWalmartReport ?
      "/api/data_report_v2/walmartsearchtermreport/stats/"
    : "/api/data_report_v2/searchtermreport/stats/";
  const dimensions =
    isWalmartReport ? "searched_keyword,match_type,bidded_keyword,item_id" : "keyword_text,query,match_type";
  const { data: searchTermReportData, isLoading: isSearchTermReportDataLoading } = useFetch(
    ["searchTermData", startDate, endDate, selectedCampaignId, adId],
    reportEndpoint,
    {
      dimensions: dimensions,
      filters: `campaign_id:${selectedCampaignId},date__gte:${startDate},date__lte:${endDate}`,
    },
    {
      default: [],
      enabled: !!selectedCampaignId && isWalmartReport,
      select: (res) => res.data,
    },
  );

  const newSearchTermReportTableColumns = useSearchTermReportColumns(dimensions);

  const defaultSelectedColumns = [
    "keyword_text",
    "query",
    "bid",
    "match_type",
    "state",
    "clicks",
    "ad_spend",
    "impressions",
    "orders",
    "ad_sales",
    "acos",
    "roas",
    "conversion_rate",
    "ctr",
    "cpc",
  ];

  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    newSearchTermReportTableColumns.filter((column) => Boolean(column.id)).map((column) => column.id),
  );

  const initialStateConfig = {
    columnVisibility: {
      ...defaultHiddenCols,
      acos: acosOrRoas(true, false),
      roas: acosOrRoas(false, true),
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      start: ["keyword_text", "query"],
      end: [],
    },
    sorting: [{ id: "impressions", desc: true }],
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `searchTermReport-mediaPlanLevel-${dimensions}`,
    initialState: initialStateConfig,
  });

  const downloadFilters = {
    start_date: startDate,
    end_date: endDate,
    campaign: selectedCampaignId,
    mp: marketPlace.marketPlace,
    channel: channel?.currentChannel,
  };

  return (
    <Row>
      <Col xs={12}>
        <Row>
          <Col className="text-center" xs={12} lg={{ offset: 4, span: 4 }}>
            <h2 className="fw-bold my-3" style={{ fontSize: "2rem", color: "#403E3D" }}>
              {t(($) => $.advertising["Search Term Report"])}
            </h2>
            {campaignOptions?.length > 1 ?
              <div
                className="mx-auto mt-5"
                style={{
                  textAlign: "start",
                  maxWidth: "200px",
                }}
              >
                <CampaignSelector
                  campaignOptions={campaignOptions}
                  selectedCampaignId={selectedCampaignId}
                  setSelectedCampaignId={setSelectedCampaignId}
                />
              </div>
            : null}
          </Col>
        </Row>
        {channel?.currentChannel === "amazon" ?
          <SearchTermReportDashboardTable
            state={state}
            initialState={initialState}
            setters={setters}
            dimensions={dimensions}
            additionalFilters={{ campaignId: selectedCampaignId }}
            columns={newSearchTermReportTableColumns}
            renderExpander={false}
            enableRowSelection={false}
            isAdPlanDetailsTable={true}
            showBulkActionsButton={true}
          />
        : <WalmartSearchTermTable
            data={searchTermReportData}
            isLoading={isSearchTermReportDataLoading || !campaignOptions?.length}
            isWalmartReport={isWalmartReport}
            isAutoCampaign={selectedCampaignId === campaigns?.auto_id}
          />
        }
      </Col>
      {userCanExportAds && channel?.currentChannel !== "amazon" && (
        <Col xs={12}>
          <DownloadReport url_path="search_term_report" filters={downloadFilters} report_dl="search_terms" />
        </Col>
      )}
    </Row>
  );
};

export default SearchTermReport;
